import React, { useEffect } from "react";
import styled, { css } from "styled-components";
import Button from "components/Button";
import { Field } from "react-final-form";
import Link from "next/link";
import PropTypes from "prop-types";
import TextInput from "fields/TextInput";
import useCustomerUser from "hooks/use-customer-user";
import { useRouter } from "next/router";
import useVendorUser from "hooks/use-vendor-user";

export const AuthFlow = ({ children }) => {
  const vendorUser = useVendorUser();
  const customerUser = useCustomerUser();
  const { replace, query } = useRouter();
  const { next } = query;
  useEffect(() => {
    if (vendorUser || customerUser) {
      if (next) {
        replace(next);
      } else {
        replace("/");
      }
    }
  });
  return <>{children}</>;
};

AuthFlow.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
};

export const AuthForm = styled.form`
  display: grid;
  padding-top: 1.5rem;
  row-gap: 1.875rem;
  width: 100%;
`;

const EmailChangeButton = styled(Button)`
  color: ${({ theme }) => theme.colors.orange};
  background-color: unset;
`;

export const FixedEmailInput = ({ userType, next, email }) => {
  const ut = { vendor: "provider" }[userType] ?? userType;
  const emailChangeLink = {
    pathname: `/${ut}/auth/sessionless`,
    query: { next, email },
  };

  return (
    <Field
      autoComplete="email"
      inputMode="email"
      label="Your Email"
      name="email"
      component={TextInput}
      type="email"
      readOnly
      defaultValue={email}
      toggleEditComponent={() => {
        return (
          <Link href={emailChangeLink} passHref>
            <EmailChangeButton type="button">Change</EmailChangeButton>
          </Link>
        );
      }}
    />
  );
};

FixedEmailInput.propTypes = {
  userType: PropTypes.string.isRequired,
  next: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
};

export const Header = styled.h2`
  font-size: 2rem;
  font-family: ${({ theme }) => theme.fontStacks.serif[0]};
  font-weight: 500;
  letter-spacing: -0.05rem;
  line-height: 1.125;
  margin: 0 0 1.5rem 0;

  ${({ noMarginBttm }) =>
    noMarginBttm &&
    css`
      margin-bottom: 0;
    `}
`;

export const SubHeader = styled(Header)`
  font-size: 1.5rem;
`;

export const ButtonWrapper = styled.div`
  display: grid;
  margin: 0;
  row-gap: 0.8125rem;
`;

const SmallStyledSpan = styled.span`
  color: ${({ theme }) => theme.themeColors.orange1};
  font-size: ${({ theme }) => theme.fontSize(2)};
  font-weight: 600;
  margin-top: -0.375rem;
  opacity: 1;
  transition: opacity 0.25s;

  :hover {
    cursor: pointer;
    opacity: 0.8;
  }
`;

export const CreateAccountButtonWrapper = styled.div`
  margin: 0 0 1.3125rem 0;
`;

export const CtaText = styled.h6`
  font-size: ${({ theme }) => theme.fontSize(2)};
  font-weight: 600;
  text-align: center;
  margin: 1rem 0;
`;

export const PasswordResetLink = ({ userType, email }) => {
  const ut = { vendor: "provider" }[userType] ?? userType;
  const params = new URLSearchParams({ email });
  const href = `/${ut}/auth/password-reset?${params}`;
  return (
    <Link href={href}>
      <SmallStyledSpan>I forgot my password</SmallStyledSpan>
    </Link>
  );
};

PasswordResetLink.propTypes = {
  userType: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
};

export const TermsPrivacy = ({ userType }) => {
  const ut = { vendor: "provider" }[userType] ?? userType;
  return (
    <CtaText>
      By continuing, you agree to GoodFriend's{" "}
      <Link href={`/${ut}/terms-service`}>
        <SmallStyledSpan>Terms of Service</SmallStyledSpan>
      </Link>{" "}
      and{" "}
      <a href="https://www.mars.com/privacy">
        <SmallStyledSpan>Privacy Policy</SmallStyledSpan>
      </a>
    </CtaText>
  );
};

TermsPrivacy.propTypes = {
  userType: PropTypes.string.isRequired,
};

export const UsertypeRedirect = ({ userType, email }) => {
  const {
    query: { next: _, ...query },
    push,
  } = useRouter();

  const onClick = (dst) => () => {
    (async () => {
      const params = {
        ...query,
        ...(email ? { email } : {}),
      };

      await push({ pathname: dst, query: params });
    })();
    return false;
  };

  if (userType === "customer") {
    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <a href="#" onClick={onClick("/provider/auth")}>
        <CtaText>
          Own a Business? <br />
          <SmallStyledSpan>Provider Log In/Sign Up</SmallStyledSpan>
        </CtaText>
      </a>
    );
  }
  if (userType === "vendor") {
    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <a href="#" onClick={onClick("/customer/auth")}>
        <CtaText>
          Want to book services for your pet?
          <br />
          <SmallStyledSpan>Log in or sign up as a pet parent</SmallStyledSpan>
        </CtaText>
      </a>
    );
  }
  return <></>;
};

UsertypeRedirect.propTypes = {
  userType: PropTypes.string.isRequired,
  email: PropTypes.string,
};

UsertypeRedirect.defaultProps = {
  email: "",
};

export const commonURIs = ({ userType }) => {
  const successUrl = {
    customer: new URL(
      // eslint-disable-next-line no-undef
      `${globalThis.location.origin}/customer/auth/email/success`
    ),
    vendor: new URL(
      // eslint-disable-next-line no-undef
      `${globalThis.location.origin}/provider/auth/email/success`
    ),
  }[userType];
  return { success_url: successUrl };
};
